import { type Location, type PropertyFiltersType } from '@aikoa/common'
import { getCurrentLocale } from '@aikoa/i18n'
import { useCurrentLanguage, useTranslations } from '@aikoa/i18n/react'
import { useCallback, useMemo, useState } from 'react'
import { View } from 'react-native'
import Animated, { LinearTransition } from 'react-native-reanimated'
import { useShallow } from 'zustand/react/shallow'
import { InterestFilterEditor } from '~/components/interests/InterestFilterEditor'
import { SelectLocations } from '~/components/interests/SelectLocations'
import { formatPrice, formatRange, formatRoomsRange } from '~/components/interests/utils'
import { useLeadStore } from '~/components/leads/leadStore'
import { Button, ButtonLabel } from '~/components/ui/button'
import {
  GuidedFormSummary,
  type InputSegment,
  type ScriptSegment,
} from '~/components/ui/guided-form-summary'
import { getBuildingTypeLabel, getConditionLabel } from '~/lib/assets'
import { propertyFilterFormObjectSchema } from '~/lib/propertyFormSchemas'

const locale = getCurrentLocale()
const { shape } = propertyFilterFormObjectSchema

type LeadFilterSegment =
  | 'locations'
  | 'price'
  | 'buildingType'
  | 'size'
  | 'rooms'
  | 'condition'
  | 'year'

const leadFilterSegmentRecord: Record<LeadFilterSegment, true> = {
  locations: true,
  price: true,
  buildingType: true,
  size: true,
  rooms: true,
  condition: true,
  year: true,
}

const allSegments = Object.keys(leadFilterSegmentRecord) as LeadFilterSegment[]

export function LeadManualFilter({
  className,
  textClassName,
  buttonClassName,
  displayOnly,
}: {
  className?: string
  textClassName?: string
  buttonClassName?: string
  displayOnly?: boolean
}) {
  const { t } = useTranslations(['leads', 'common'])
  const { t: tInterest } = useTranslations('interest')
  const { t: tProperty } = useTranslations('property')

  const language = useCurrentLanguage()

  const { manualFilter, setManualFilter, setOfferAssetId } = useLeadStore(
    useShallow((state) => ({
      manualFilter: state.manualFilter,
      setManualFilter: state.setManualFilter,
      setOfferAssetId: state.setOfferAssetId,
    }))
  )

  const { filters, locations } = manualFilter

  const [selectedEditor, setSelectedEditor] = useState<LeadFilterSegment | null>(null)

  const handleFiltersChange = useCallback(
    (nextFilters: PropertyFiltersType) => {
      if (Object.keys(nextFilters).length > 0) {
        setOfferAssetId(undefined)
      }

      setManualFilter((current) => {
        const normalizedFilters = { ...nextFilters }

        if (normalizedFilters.buildingTypes?.length) {
          normalizedFilters.buildingTypes = [normalizedFilters.buildingTypes[0]]
        }

        if (normalizedFilters.conditions?.length) {
          normalizedFilters.conditions = [normalizedFilters.conditions[0]]
        }

        return {
          ...current,
          filters: normalizedFilters,
        }
      })
    },
    [setManualFilter, setOfferAssetId]
  )

  const handleLocationsChange = useCallback(
    (nextLocations: Location[]) => {
      const filteredLocations = nextLocations.filter((location) => !!location.coordinates)

      if (filteredLocations.length > 0) {
        setOfferAssetId(undefined)
      }

      setManualFilter((current) => ({
        ...current,
        locations: filteredLocations,
      }))
    },
    [setManualFilter, setOfferAssetId]
  )

  const buildingTypes = useMemo(() => filters.buildingTypes ?? [], [filters.buildingTypes])

  const conditions = useMemo(() => filters.conditions ?? [], [filters.conditions])

  const buildingTypeLabels = useMemo(
    () => buildingTypes.map((type) => getBuildingTypeLabel({ buildingType: type, t: tProperty })),
    [buildingTypes, tProperty]
  )

  const conditionLabels = useMemo(
    () => conditions.map((cond) => getConditionLabel(cond, tProperty)),
    [conditions, tProperty]
  )

  const locLabels = useMemo(() => locations.map((loc) => loc.label), [locations])

  const formatList = useCallback((items: string[]) => {
    if (items.length > 3) {
      return `${items.slice(0, 3).join(', ')} +${items.length - 3}`
    }
    return items.join(', ')
  }, [])

  const inputs: InputSegment<LeadFilterSegment>[] = useMemo(
    () => [
      {
        name: 'locations',
        value: locations,
        editor: null,
        isValid: () => locLabels.length > 0,
        renderValue: () => (locLabels.length > 0 ? formatList(locLabels) : undefined),
        optional: true,
      },
      {
        name: 'price',
        value: { priceMin: filters.priceMin, priceMax: filters.priceMax },
        editor: null,
        isValid: () => filters.priceMin !== undefined || filters.priceMax !== undefined,
        hasError: () =>
          (filters.priceMax !== undefined &&
            !shape.priceMax.safeParse(filters.priceMax).success) ||
          (filters.priceMin !== undefined &&
            !shape.priceMin.safeParse(filters.priceMin).success) ||
          (filters.priceMin !== undefined &&
            filters.priceMax !== undefined &&
            filters.priceMin > filters.priceMax),
        renderValue: () =>
          filters.priceMin || filters.priceMax
            ? formatPrice(language, tInterest, filters.priceMin, filters.priceMax)
            : undefined,
        optional: true,
      },
      {
        name: 'buildingType',
        value: buildingTypes,
        editor: null,
        isValid: () => buildingTypes.length > 0,
        renderValue: () =>
          buildingTypeLabels.length > 0 ? formatList(buildingTypeLabels) : undefined,
        optional: true,
      },
      {
        name: 'size',
        value: { sizeMin: filters.sizeMin, sizeMax: filters.sizeMax },
        editor: null,
        isValid: () => filters.sizeMin !== undefined || filters.sizeMax !== undefined,
        hasError: () =>
          (filters.sizeMin !== undefined && !shape.sizeMin.safeParse(filters.sizeMin).success) ||
          (filters.sizeMax !== undefined && !shape.sizeMax.safeParse(filters.sizeMax).success) ||
          (filters.sizeMin !== undefined &&
            filters.sizeMax !== undefined &&
            filters.sizeMin > filters.sizeMax),
        renderValue: () =>
          filters.sizeMin || filters.sizeMax
            ? formatRange(
                filters.sizeMin,
                filters.sizeMax,
                tInterest(($) => $.details.sizeUnit)
              )
            : undefined,
        optional: true,
      },
      {
        name: 'rooms',
        value: { roomsMin: filters.roomsMin, roomsMax: filters.roomsMax },
        editor: null,
        isValid: () => filters.roomsMin !== undefined || filters.roomsMax !== undefined,
        hasError: () =>
          (filters.roomsMin !== undefined &&
            !shape.roomsMin.safeParse(filters.roomsMin).success) ||
          (filters.roomsMax !== undefined &&
            !shape.roomsMax.safeParse(filters.roomsMax).success) ||
          (filters.roomsMin !== undefined &&
            filters.roomsMax !== undefined &&
            filters.roomsMin > filters.roomsMax),
        renderValue: () =>
          filters.roomsMin || filters.roomsMax
            ? formatRoomsRange(filters.roomsMin, filters.roomsMax)
            : undefined,
        optional: true,
      },
      {
        name: 'condition',
        value: conditions,
        editor: null,
        isValid: () => conditions.length > 0,
        renderValue: () => (conditionLabels.length > 0 ? formatList(conditionLabels) : undefined),
        optional: true,
      },
      {
        name: 'year',
        value: {
          yearMin: filters.buildYearMin,
          yearMax: filters.buildYearMax,
        },
        editor: null,
        isValid: () => filters.buildYearMin !== undefined || filters.buildYearMax !== undefined,
        hasError: () => {
          const currentYear = new Date().getFullYear()
          const yearMin = filters.buildYearMin ? Number(filters.buildYearMin) : undefined
          const yearMax = filters.buildYearMax ? Number(filters.buildYearMax) : undefined

          return (
            (yearMin !== undefined &&
              (yearMin < locale.property.min_year || yearMin > currentYear)) ||
            (yearMax !== undefined &&
              (yearMax < locale.property.min_year || yearMax > currentYear))
          )
        },
        renderValue: () =>
          filters.buildYearMin || filters.buildYearMax
            ? formatRange(filters.buildYearMin, filters.buildYearMax)
            : undefined,
        optional: true,
      },
    ],
    [
      locations,
      locLabels,
      filters,
      buildingTypes,
      buildingTypeLabels,
      conditions,
      conditionLabels,
      formatList,
      tInterest,
      language,
    ]
  )

  const script: ScriptSegment<LeadFilterSegment>[] = useMemo(
    () => [
      {
        name: 'locations',
        firstSegmentScript: t(($) => $.filters.manualSearch.script.locations.first),
        segmentScript: t(($) => $.filters.manualSearch.script.locations.middle),
        buttonLabel: t(($) => $.filters.manualSearch.locations),
      },
      {
        name: 'price',
        firstSegmentScript: t(($) => $.filters.manualSearch.script.price.first),
        segmentScript: t(($) => $.filters.manualSearch.script.price.middle),
        lastSegmentScript: t(($) => $.filters.manualSearch.script.price.last),
        buttonLabel: t(($) => $.filters.manualSearch.price),
      },
      {
        name: 'buildingType',
        firstSegmentScript: t(($) => $.filters.manualSearch.script.buildingType.first),
        segmentScript: t(($) => $.filters.manualSearch.script.buildingType.middle),
        lastSegmentScript: t(($) => $.filters.manualSearch.script.buildingType.last),
        buttonLabel: t(($) => $.filters.manualSearch.buildingType),
      },
      {
        name: 'size',
        firstSegmentScript: t(($) => $.filters.manualSearch.script.size.first),
        segmentScript: t(($) => $.filters.manualSearch.script.size.middle),
        lastSegmentScript: t(($) => $.filters.manualSearch.script.size.last),
        buttonLabel: t(($) => $.filters.manualSearch.size),
      },
      {
        name: 'rooms',
        firstSegmentScript: t(($) => $.filters.manualSearch.script.rooms.first, {
          rooms: filters.roomsMin ?? 0,
        }),
        segmentScript: t(($) => $.filters.manualSearch.script.rooms.middle, {
          rooms: filters.roomsMin ?? 0,
        }),
        lastSegmentScript: t(($) => $.filters.manualSearch.script.rooms.last, {
          rooms: filters.roomsMin ?? 0,
        }),
        buttonLabel: t(($) => $.filters.manualSearch.rooms),
      },
      {
        name: 'condition',
        firstSegmentScript: t(($) => $.filters.manualSearch.script.condition.first),
        segmentScript: t(($) => $.filters.manualSearch.script.condition.middle),
        lastSegmentScript: t(($) => $.filters.manualSearch.script.condition.last),
        buttonLabel: t(($) => $.filters.manualSearch.condition),
      },
      {
        name: 'year',
        firstSegmentScript: t(($) => $.filters.manualSearch.script.year.first),
        segmentScript: t(($) => $.filters.manualSearch.script.year.middle),
        lastSegmentScript: t(($) => $.filters.manualSearch.script.year.last),
        buttonLabel: t(($) => $.filters.manualSearch.year),
      },
    ],
    [t, filters.roomsMin]
  )

  const handleSelectSegment = useCallback((segment: LeadFilterSegment | undefined) => {
    setSelectedEditor(segment ?? null)
  }, [])

  const renderEditor = useCallback(
    (segment: LeadFilterSegment) => (
      <Animated.View
        key={`editor-${segment}`}
        className="my-2 w-full gap-4 rounded-content bg-muted px-4 py-6 sm:px-6"
        layout={LinearTransition.duration(150)}>
        <View className="relative z-50 flex-1">
          {segment === 'locations' ? (
            <SelectLocations
              inputVariant="line-dark"
              initialLocations={locations}
              onChangeLocations={handleLocationsChange}
              biasCoordinates={locations[0]?.coordinates ?? null}
            />
          ) : (
            <InterestFilterEditor
              editor={segment}
              filters={filters}
              onChange={handleFiltersChange}
            />
          )}
        </View>
        <Button
          variant="link"
          size="sm"
          className="relative z-0 -mb-2"
          onPress={() => setSelectedEditor(null)}>
          <ButtonLabel>{tProperty(($) => $.flow.manualInput.close)}</ButtonLabel>
        </Button>
      </Animated.View>
    ),
    [locations, filters, handleLocationsChange, handleFiltersChange, tProperty]
  )

  const hasAnyValues = inputs.some((input) => input.isValid())

  return (
    <GuidedFormSummary
      inputs={inputs}
      script={script}
      displayMode={displayOnly ? 'display-valid' : 'edit'}
      buttonClassName={buttonClassName}
      prefixScript={t(($) => $.filters.manualSearch.prefixScript) + (!hasAnyValues ? '...' : '')}
      activatedSegments={allSegments}
      selectedSegment={selectedEditor ?? undefined}
      onSelectSegment={handleSelectSegment}
      renderEditor={renderEditor}
      className={className}
      textClassName={textClassName}
    />
  )
}
